import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import {
  BoltIcon, HomeIcon, TagIcon, RectangleGroupIcon, ArrowLeftOnRectangleIcon,
  UserCircleIcon, Bars3Icon, XMarkIcon, ArrowUturnLeftIcon, PlusIcon, WrenchScrewdriverIcon,
  ShieldExclamationIcon, ClipboardDocumentListIcon, Cog6ToothIcon, ChevronDownIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import api from '../services/api';
import BikeList from '../components/BikeList';
import BikeCatalog from '../components/BikeCatalog';
import BikePratichePage from '../components/bike/BikePratichePage';
import BikePraticaDetail from '../components/bike/BikePraticaDetail';
import BikeImpostazioni from '../components/bike/BikeImpostazioni';
import NotificheBell from '../components/notifiche/NotificheBell';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// `agent: true` = voce visibile anche ai non-admin (agenti). Le altre sono solo admin.
const NAV = [
  { to: '/bike', label: 'Dashboard', icon: HomeIcon, exact: true, agent: true },
  { to: '/bike/pratiche', label: 'Pratiche', icon: ClipboardDocumentListIcon, agent: true },
  { to: '/bike/bici', label: 'Gestione Bici', icon: BoltIcon },
  { to: '/bike/catalogo', label: 'Catalogo', icon: RectangleGroupIcon, agent: true },
  { to: '/bike/impostazioni', label: 'Impostazioni', icon: Cog6ToothIcon }
];

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
};

// ===== Landing dashboard bici =====
function BikeHome({ user, isAdmin }) {
  const [stats, setStats] = useState({ total: 0, numBrand: 0, valoreTotale: 0, prezzoMedio: 0, perBrand: [] });
  const [praticheAperte, setPraticheAperte] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/bici/stats');
        setStats({
          total: data.total || 0,
          numBrand: data.numBrand || 0,
          valoreTotale: data.valoreTotale || 0,
          prezzoMedio: data.prezzoMedio || 0,
          perBrand: data.perBrand || []
        });
      } catch (e) { /* ignora */ }
      try {
        const { data } = await api.get('/bici-pratiche/stats');
        setPraticheAperte(data?.aperte || 0);
      } catch (e) { /* ignora */ }
      setTimeout(() => setIsLoaded(true), 50);
    })();
  }, []);

  const euro = (v) => '€ ' + (Number(v) || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 });

  const topBrand = stats.perBrand.slice(0, 12);
  const barData = {
    labels: topBrand.map(b => b.brand),
    datasets: [{
      label: 'Prodotti',
      data: topBrand.map(b => b.conteggio),
      backgroundColor: 'rgba(16,185,129,0.75)',
      borderRadius: 8,
      maxBarThickness: 40
    }]
  };
  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(0,0,0,0.05)' } },
      x: { grid: { display: false } }
    }
  };

  const KPI = ({ label, value, sub, icon: Icon, color, to }) => {
    const inner = (
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 border border-white/40 shadow-lg shadow-black/5 h-full hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
  };

  const Azione = ({ to, label, icon: Icon, presto }) => (
    <Link to={presto ? '#' : to} onClick={(e) => presto && e.preventDefault()}
      className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${
        presto ? 'border-dashed border-gray-300 bg-gray-50/60 cursor-default'
        : 'border-white/40 bg-white/70 backdrop-blur-xl shadow-lg shadow-black/5 hover:shadow-xl hover:-translate-y-0.5'
      }`}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${presto ? 'bg-gray-100 text-gray-400' : 'bg-emerald-100 text-emerald-600'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className={`font-semibold ${presto ? 'text-gray-500' : 'text-gray-900'}`}>{label}</p>
        {presto && <p className="text-[11px] text-gray-400">Prossima fase</p>}
      </div>
    </Link>
  );

  return (
    <div className="relative min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50 via-white to-emerald-50/40">
      {/* pattern a puntini */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(16,185,129,0.06)_1px,transparent_0)] [background-size:24px_24px] pointer-events-none" />

      <div className={`relative z-10 max-w-[1800px] mx-auto px-4 md:px-6 py-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        {/* Saluto */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/40 shadow-xl shadow-black/5 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                <BoltIcon className="h-4 w-4" /> Bike Mobility
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                {getGreeting()}{user?.nome ? `, ${user.nome}` : ''} 👋
              </h1>
              <p className="text-gray-500 mt-1">Ecco la panoramica di vendite e catalogo bici.</p>
            </div>
            <Link to="/bike/pratiche"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-colors">
              <PlusIcon className="h-5 w-5" /> Nuova pratica
            </Link>
          </div>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPI label="Pratiche aperte" value={praticheAperte} icon={ClipboardDocumentListIcon} color="bg-emerald-100 text-emerald-600" to="/bike/pratiche" />
          <KPI label="Bici in catalogo" value={stats.total} icon={BoltIcon} color="bg-teal-100 text-teal-600" to="/bike/catalogo" />
          <KPI label="Valore catalogo" value={euro(stats.valoreTotale)} sub="prezzo al pubblico" icon={RectangleGroupIcon} color="bg-emerald-100 text-emerald-600" to="/bike/catalogo" />
          <KPI label="Prezzo medio" value={euro(stats.prezzoMedio)} icon={WrenchScrewdriverIcon} color="bg-slate-100 text-slate-500" />
        </div>

        {/* Grafico prodotti per brand */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/40 shadow-xl shadow-black/5 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Prodotti per brand</h3>
          <div className="h-64">
            {topBrand.length > 0 ? <Bar data={barData} options={barOptions} />
              : <div className="h-full flex items-center justify-center text-gray-400 text-sm">Nessun dato — importa o aggiungi bici</div>}
          </div>
        </div>

        {/* Azioni rapide */}
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Azioni rapide</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Azione to="/bike/pratiche" label="Pratiche di vendita" icon={ClipboardDocumentListIcon} />
          {isAdmin && <Azione to="/bike/bici" label="Gestione Bici" icon={BoltIcon} />}
          <Azione to="/bike/catalogo" label="Catalogo prodotti" icon={RectangleGroupIcon} />
        </div>
      </div>
    </div>
  );
}

// ===== Shell dashboard bici =====
export default function BikeDashboard() {
  const { user, logout } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const initials = (user && user.nome && user.cognome)
    ? `${user.nome.charAt(0)}${user.cognome.charAt(0)}`.toUpperCase()
    : (user?.username?.charAt(0)?.toUpperCase() || 'U');

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  // Admin = accesso completo; gli altri ruoli (agenti) accedono a Dashboard/Pratiche/Catalogo
  const isAdmin = ['admin'].includes(String(user?.ruolo || user?.role || '').toLowerCase());
  const nav = NAV.filter((i) => isAdmin || i.agent);

  // Schermata per le sezioni riservate agli admin
  const Riservato = () => (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/40 p-8">
        <ShieldExclamationIcon className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Sezione riservata</h2>
        <p className="text-gray-500 mb-6">Questa sezione è disponibile solo per gli amministratori.</p>
        <button onClick={() => navigate('/bike/pratiche')}
          className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors">
          Vai alle pratiche
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
      {/* HEADER */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo WAPOO + modulo Bike */}
            <Link to="/bike" className="flex items-center gap-3">
              <img src="/logo.png" alt="WAPOO" className="h-8 sm:h-9 w-auto" />
              <div className="leading-tight border-l border-gray-200 pl-3">
                <div className="font-semibold text-base flex items-center gap-1 text-emerald-600">
                  <BoltIcon className="h-4 w-4" /> Bike
                </div>
                <div className="text-[10px] text-gray-400 -mt-0.5 tracking-wide">BIKE MOBILITY</div>
              </div>
            </Link>

            {/* Nav desktop */}
            <nav className="hidden lg:flex items-center gap-1">
              {nav.map((item) => (
                <Link key={item.to} to={item.presto ? '#' : item.to}
                  onClick={(e) => item.presto && e.preventDefault()}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item) ? 'bg-emerald-50 text-emerald-700'
                    : item.presto ? 'text-gray-400 cursor-default'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}>
                  {item.label}
                  {item.presto && <span className="text-[9px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full">presto</span>}
                </Link>
              ))}
            </nav>

            {/* Destra */}
            <div className="flex items-center gap-2">
              <NotificheBell user={user} accent="emerald" modulo="bici" />
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex items-center gap-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all">
                  <span className="h-9 w-9 rounded-full bg-gray-800 text-white text-sm font-semibold flex items-center justify-center">{initials}</span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl ring-1 ring-gray-200 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-900 truncate">{user?.nome} {user?.cognome}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email || user?.username}</p>
                      {(user?.ruolo || user?.role) && (
                        <span className="mt-1.5 inline-block text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{user?.ruolo || user?.role}</span>
                      )}
                    </div>
                    <button onClick={() => { setUserMenuOpen(false); navigate('/profilo'); }}
                      className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50/60 rounded-xl mx-1">
                      <UserCircleIcon className="h-4 w-4" /> Profilo
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); navigate('/nlt'); }}
                      className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50/60 rounded-xl mx-1">
                      <ArrowUturnLeftIcon className="h-4 w-4" /> Torna a NLT
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button onClick={() => { setUserMenuOpen(false); logout(); }}
                      className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-xl mx-1">
                      <ArrowLeftOnRectangleIcon className="h-4 w-4" /> Esci
                    </button>
                  </div>
                )}
              </div>

              <button onClick={() => setMobileOpen((o) => !o)} className="lg:hidden p-2 text-gray-600">
                {mobileOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <nav className="lg:hidden pb-3 space-y-1">
              {nav.map((item) => (
                <Link key={item.to} to={item.presto ? '#' : item.to}
                  onClick={(e) => { if (item.presto) e.preventDefault(); else setMobileOpen(false); }}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold ${
                    isActive(item) ? 'bg-emerald-50 text-emerald-700'
                    : item.presto ? 'text-gray-400' : 'text-gray-700 hover:bg-emerald-50/60'
                  }`}>
                  <item.icon className="h-4 w-4" /> {item.label}
                  {item.presto && <span className="text-[9px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full">presto</span>}
                </Link>
              ))}
              <Link to="/nlt" className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-gray-500">
                <ArrowUturnLeftIcon className="h-4 w-4" /> Torna a NLT
              </Link>
            </nav>
          )}
        </div>
      </header>

      {/* CONTENUTO */}
      <main>
        <Routes>
          <Route path="/" element={<BikeHome user={user} isAdmin={isAdmin} />} />
          <Route path="/pratiche" element={<BikePratichePage />} />
          <Route path="/pratiche/:id" element={<BikePraticaDetail />} />
          <Route path="/bici" element={isAdmin ? <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6"><BikeList /></div> : <Riservato />} />
          <Route path="/catalogo" element={<BikeCatalog />} />
          <Route path="/impostazioni" element={isAdmin ? <BikeImpostazioni /> : <Riservato />} />
          <Route path="*" element={<BikeHome user={user} isAdmin={isAdmin} />} />
        </Routes>
      </main>
    </div>
  );
}
